import { Button } from '@/components/ui/button';
import { event } from '@/lib/google-analytics';
import { project } from '@/lib/project';
import { fetcher } from '@/lib/utils';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { IoTicket } from 'react-icons/io5';
import { MdStar } from 'react-icons/md';
import { i18nRoute } from 'routes';
import useSWR from 'swr';

export default function AttractionsWidget({ destination, size = 5 }) {
	const { t } = useTranslation('common');
	const router = useRouter();
	const currency = project.currencies[router.locale];
	const url = `${process.env.NEXT_PUBLIC_TRAVEL_API_ENDPOINT}/attractions/${destination.id}/top?currency=${currency}&limit=${size}`;
	const { data, isLoading } = useSWR(url, fetcher);
	if (!data || isLoading) return null;
	if (
		!data ||
		!data.data ||
		!data.data.attractions ||
		data.data.attractions.length === 0
	) {
		return null;
	}

	const handleAllAttractionsClick = () => {
		event('bb_things_to_do_ATTRACTION_WIDGET_SEE_ALL_CLICK', {
			category: destination.longName,
			label: 'See all',
		});
	};

	return (
		<div className="ml-auto w-[300px]">
			<h2 className="mb-4 text-2xl font-bold tracking-tight text-gray-900">
				{t('attractions.title', {
					destination: destination.shortName,
				})}
			</h2>
			{data.data.attractions.map((attraction) => (
				<div key={attraction.attractionId}>
					<AttractionWidgetItem
						attraction={attraction}
						destination={destination}
						locale={router.locale}
					/>
				</div>
			))}
			<Button
				asChild
				className={'w-full'}
				size={'lg'}
				onClick={handleAllAttractionsClick}
			>
				<Link
					href={i18nRoute('/things-to-do/[destinationId]', router.locale, {
						destinationId: destination.slug,
					})}
				>
					{t('attractions.buttonExplore')}
				</Link>
			</Button>
		</div>
	);
}

export function AttractionWidgetItem({ destination, attraction, locale }) {
	const handleAttractionClick = () => {
		event('bb_things_to_do_ATTRACTION_WIDGET_CLICK', {
			category: destination.longName,
			label: attraction.name,
		});
	};

	return (
		<div className="group relative flex gap-x-4 space-y-4">
			<div className="min-w-28 relative aspect-video overflow-hidden rounded-lg">
				<Image
					alt={attraction.name}
					width={828}
					height={446}
					sizes={
						'(min-width: 1360px) 797px, (min-width: 1040px) calc(53.33vw + 82px), calc(100vw - 40px)'
					}
					className="size-full absolute inset-0 cursor-pointer object-cover transition-transform group-hover:scale-105"
					src={attraction.pictures[0]}
				/>
			</div>
			<div className="">
				<Link
					onClick={handleAttractionClick}
					href={i18nRoute(
						'/things-to-do/[destinationId]/attractions/[attractionId]',
						locale,
						{
							destinationId: destination.slug,
							attractionId: attraction.attractionId,
						}
					)}
				>
					<h3 className="line-clamp-2 text-sm font-semibold leading-tight group-hover:cursor-pointer group-hover:underline">
						{attraction.name}
					</h3>
				</Link>
				<div className="mt-2 flex items-center gap-x-4">
					<div className="flex items-center gap-x-0.5">
						<MdStar className="size-5 text-yellow-500" />
						<span className="text-sm text-gray-500">
							{attraction.rating > 0 ? attraction.rating.toFixed(1) : 'N/A'}
						</span>
					</div>
					<div className="flex items-center gap-x-0.5">
						<IoTicket className="size-4 text-yellow-500" />
						<span className="text-sm text-gray-500">
							{attraction.price.amount.toFixed(0)}
							<small className="ml-0.5 inline-block text-xs text-gray-500">
								{attraction.price.currencyCode}
							</small>
						</span>
					</div>
				</div>
			</div>
		</div>
	);
}
